import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface RevealProps {
    children: ReactNode
    className?: string
    delay?: number
}

// Fades and lifts its children in the first time they scroll into view.
// Use it for content below the fold only; the hero animates from CSS.
export const Reveal: FC<RevealProps> = ({ children, className, delay = 0 }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        if (typeof IntersectionObserver === 'undefined') {
            setVisible(true)
            return
        }

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) {
                setVisible(true)
                observer.disconnect()
            }
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })

        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    const style: CSSProperties | undefined = delay ? { transitionDelay: `${delay}ms` } : undefined

    return (
        <div ref={ref} className={cn('reveal', visible && 'is-visible', className)} style={style}>
            {children}
        </div>
    )
}
